<template>
    <div class="cpf-card" v-if="employee">
        <!-- Card Header -->
        <div class="cpf-header">
            <div class="cpf-header-left">
                <div class="cpf-avatar">{{ (employee.firstName || '?')[0].toUpperCase() }}</div>
                <div>
                    <div class="cpf-name">{{ employee.firstName }} {{ employee.lastName }}</div>
                    <router-link class="cpf-edit-link" target="_blank" :to="`/employees/edit/${employee.id}?active_tab=PAYROLL`">
                        <i class="fas fa-external-link-alt"></i> Edit Payroll
                    </router-link>
                </div>
            </div>
            <div class="cpf-totals">
                <div class="cpf-total-item cpf-total-earn">
                    <span class="cpf-total-label">Earnings</span>
                    <span class="cpf-total-value">₹{{ totalAmount.toLocaleString() }}</span>
                </div>
                <div class="cpf-total-item cpf-total-ded">
                    <span class="cpf-total-label">Deductions</span>
                    <span class="cpf-total-value">₹{{ totalDeductions.toLocaleString() }}</span>
                </div>
                <div class="cpf-total-item cpf-total-net">
                    <span class="cpf-total-label">Net Pay</span>
                    <span class="cpf-total-value">₹{{ netTotal.toLocaleString() }}</span>
                </div>
            </div>
        </div>

        <!-- Payhead Table -->
        <div class="cpf-table">
            <div class="cpf-table-head">
                <span>Payhead</span>
                <span>Amount</span>
                <span>Type</span>
            </div>
            <div class="cpf-table-body">
                <div v-for="epdi of employee.payheadValues" :key="epdi.id" class="cpf-row" v-if="epdi.payheadValue.enabled">
                    <div class="cpf-row-name">
                        {{ epdi.payhead.name }}
                        <span class="cpf-row-sub" v-if="subtitleMsg(epdi)">{{ subtitleMsg(epdi) }}</span>
                    </div>
                    <div class="cpf-row-input-wrap">
                        <span class="cpf-rupee">₹</span>
                        <input
                            :disabled="isInputDisabled(epdi)"
                            :class="['cpf-input', { 'cpf-input-disabled': isInputDisabled(epdi) }]"
                            v-model="epdi.inputAmount"
                            type="number"
                        />
                    </div>
                    <div>
                        <span :class="['cpf-type-pill', epdi.payheadType.isDecrement ? 'cpf-type-ded' : 'cpf-type-earn']">
                            {{ epdi.payheadType.isDecrement ? 'Deduction' : 'Earning' }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Warning -->
        <div class="cpf-warning" v-if="netTotal <= 0">
            <i class="fas fa-exclamation-triangle"></i> Net total is zero or negative. Please review the amounts.
        </div>
    </div>
</template>

<script>
import { round2 } from "../../utils/common"

export default {
    props: {
        employee: { type: Object, default: () => null },
    },
    data() {
        return {
            inputValues: [],
        }
    },
    computed: {
        netTotal() {
            const val = round2(this.totalAmount - this.totalDeductions)
            return val < 0 ? 0 : val
        },
        totalAmount() {
            return round2(
                this.employee.payheadValues.reduce((val, epdi) => val + (epdi.payheadType.isDecrement ? 0 : Number(epdi.inputAmount)), 0)
            )
        },
        totalDeductions() {
            return round2(
                this.employee.payheadValues.reduce((val, epdi) => val + (epdi.payheadType.isDecrement ? Number(epdi.inputAmount) : 0), 0)
            )
        },
    },
    methods: {
        round2,
        isInputDisabled(epdi) {
            return false
            if (epdi.payhead.isLeavePayhead || epdi.payhead.isOvertimePayhead || epdi.payhead.disableCompanyAndEmployeeEdits) {
                return true
            } else if (epdi.calculationType) {
                return !epdi.calculationType.isUserDefined
            } else {
                return false
            }
        },
        subtitleMsg(epdi) {
            if (
                !epdi.payhead.defaultAmount &&
                !epdi.payhead.defaultPercentage &&
                (epdi.payhead.isLeavePayhead ||
                    epdi.payhead.isOvertimePayhead ||
                    (epdi.calculationType && epdi.calculationType.isComputedValue))
            ) {
                const amount = epdi.payheadValue.value || epdi.payheadValue.percentage || 0
                let sign = ""

                if (epdi.payheadValue.percentage) {
                    sign = "%"
                } else if (epdi.payhead.isLeavePayhead) {
                    sign = " rate/day"
                } else if (epdi.payhead.isOvertimePayhead) {
                    sign = " rate/hr"
                }
                return `(${amount}${sign})`
            }
            return ""
        },
        getData() {
            const payheadValues = []

            for (const epdi of this.employee.payheadValues) {
                payheadValues.push({
                    payheadId: epdi.payhead.id,
                    calculationTypeId: epdi.calculationType && epdi.calculationType.id,
                    value: epdi.payheadValue.value,
                    percentage: epdi.payheadValue.percentage,
                    calculateFromPayheadId: epdi.payheadValue.calculateFromPayheadId,
                    title: epdi.payhead.name,
                    subTitle: this.subtitleMsg(epdi),
                    finalAmount: epdi.inputAmount,
                    isDecrement: epdi.payheadType.isDecrement,
                })
            }
            return {
                payheadValues,
                employeeId: this.employee.id,
                netSalaryAmount: this.netTotal,
                totalEarningAmount: this.totalAmount,
                totalDeductionAmount: this.totalDeductions,
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.cpf-card { background:white; border:1px solid $gray100; border-radius:10px; overflow:hidden; box-shadow:$shadowSm; }

/* Header */
.cpf-header { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid $gray100; gap:12px; flex-wrap:wrap; }
.cpf-header-left { display:flex; align-items:center; gap:10px; }
.cpf-avatar { width:36px; height:36px; border-radius:$radiusFull; background:linear-gradient(135deg, $primaryColor, $primaryDark); color:white; display:flex; align-items:center; justify-content:center; font-size:14px; font-weight:700; flex-shrink:0; }
.cpf-name { font-size:14px; font-weight:700; color:$gray800; text-transform:capitalize; line-height:1.2; }
.cpf-edit-link { font-size:11.5px; color:$primaryColor; text-decoration:none; font-weight:500; display:inline-flex; align-items:center; gap:4px; i { font-size:9px; } &:hover { text-decoration:underline; } }
.cpf-totals { display:flex; gap:6px; }
.cpf-total-item { display:flex; flex-direction:column; align-items:center; padding:6px 14px; border-radius:8px; min-width:90px; }
.cpf-total-label { font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:.4px; margin-bottom:1px; }
.cpf-total-value { font-size:13px; font-weight:700; }
.cpf-total-earn { background:rgba(16,185,129,.08); .cpf-total-label { color:#059669; } .cpf-total-value { color:#047857; } }
.cpf-total-ded { background:rgba(239,68,68,.08); .cpf-total-label { color:#dc2626; } .cpf-total-value { color:#b91c1c; } }
.cpf-total-net { background:rgba(0,94,137,.08); .cpf-total-label { color:$primaryColor; } .cpf-total-value { color:$primaryDark; } }

/* Table */
.cpf-table { padding:0 18px 14px; }
.cpf-table-head { display:grid; grid-template-columns:1.5fr 1fr .7fr; gap:10px; padding:8px 0; border-bottom:1px solid $gray100; span { font-size:10.5px; font-weight:700; text-transform:uppercase; color:$gray400; letter-spacing:.4px; } }
.cpf-table-body { }
.cpf-row { display:grid; grid-template-columns:1.5fr 1fr .7fr; gap:10px; align-items:center; padding:8px 0; border-bottom:1px solid $gray50; &:last-child { border-bottom:none; } }
.cpf-row-name { font-size:13px; font-weight:500; color:$gray700; }
.cpf-row-sub { font-size:11px; color:$gray400; font-weight:400; margin-left:4px; }
.cpf-row-input-wrap { display:flex; align-items:center; gap:0; }
.cpf-rupee { font-size:13px; color:$gray500; padding:6px 8px; background:$gray50; border:1px solid $gray200; border-right:none; border-radius:6px 0 0 6px; line-height:1; font-weight:600; }
.cpf-input { width:90px; border:1px solid $gray200; border-left:none; border-radius:0 6px 6px 0; padding:6px 8px; font-size:13px; font-weight:500; color:$gray800; outline:none; background:white; font-family:inherit; transition:border $transitionFast; -moz-appearance:textfield; &::-webkit-outer-spin-button, &::-webkit-inner-spin-button { -webkit-appearance:none; margin:0; } &:focus { border-color:$primaryColor; } &.cpf-input-disabled { background:$gray50; color:$gray400; cursor:not-allowed; } }
.cpf-type-pill { font-size:11px; font-weight:600; padding:3px 10px; border-radius:$radiusFull; display:inline-block; }
.cpf-type-earn { background:rgba(16,185,129,.1); color:#059669; }
.cpf-type-ded { background:rgba(239,68,68,.1); color:#dc2626; }

/* Warning */
.cpf-warning { margin:0 18px 14px; padding:8px 12px; border-radius:6px; background:rgba(245,158,11,.08); border:1px solid rgba(245,158,11,.2); font-size:12px; font-weight:500; color:#b45309; i { margin-right:4px; font-size:11px; } }

@media(max-width:768px) {
    .cpf-header { flex-direction:column; align-items:flex-start; }
    .cpf-totals { width:100%; }
    .cpf-total-item { flex:1; min-width:0; padding:6px 8px; }
    .cpf-table-head, .cpf-row { grid-template-columns:1.2fr 1fr .8fr; }
}
</style>
